/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";

import { Form, Formik, FormikConfig, FormikHelpers } from "formik";
import { Link, navigate, RouteComponentProps, useParams } from "@reach/router";
import classNames from "classnames";

import { LinkApp as LinkAppType, LinkAppPrefill, LinkAppPrefillVariables, LinkAppVariables } from "types/gql-types";
import LinkAppFields from "../components/link-app-fields";
import { linkAppSubmissionMutation, linkAppPrefillQuery } from "../queries";

import { linkAppFormSchema } from "../schemas";


import ErrorMessage from "../components/error-message";

import styles from "../link-app.module.css";

import Loading from "../components/loading";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  linkCode: "",
  terms: false,
};
export type Values = typeof initialValues;

enum ErrorCodes {
  DuplicateAccount = "name_mismatch",
  InvalidPhoneNumber = "invalid_phone_number",
  AlreadyLinked = "already_linked",
  UnknownCode = "unknown_code",
  GeneralError = "general_error"
}

const useErrors = () => {
  const [errorCode, setErrorCode] = useState<ErrorCodes>();

  const setError = (serverCode: string, formikHelpers: FormikHelpers<Values>) => {
    switch (serverCode) {
      case ErrorCodes.DuplicateAccount:
        setErrorCode(ErrorCodes.DuplicateAccount);
        formikHelpers.setFieldError("firstName", "Mistyped?");
        formikHelpers.setFieldError("lastName", "Mistyped?");
        formikHelpers.setFieldError("email", "Already exists");
        formikHelpers.setFieldError("phone", "Already exists");
        break;

      case ErrorCodes.AlreadyLinked:
        setErrorCode(ErrorCodes.AlreadyLinked);
        formikHelpers.setFieldError("linkCode", "Code already used");
        break;

      case ErrorCodes.UnknownCode:
        setErrorCode(ErrorCodes.UnknownCode);
        formikHelpers.setFieldError("linkCode", "Code not found");
        break;

      case ErrorCodes.InvalidPhoneNumber:
        setErrorCode(ErrorCodes.InvalidPhoneNumber);
        formikHelpers.setFieldError("phone", "Invalid number");
        break;

      default:
        setErrorCode(ErrorCodes.GeneralError);
    }
  };

  const hook = [errorCode, setError, setErrorCode] as const;
  return hook;
};

const formErrorEmail = (errorDetail: string, values: Values) => `If you have any additional details for support, please add them here.


Please do not delete the text below, to help support better diagnose
and help resolve your support request:

Error: ${errorDetail} while linking applications

Form Details:
  - Link Code: ${values.linkCode}
  - First Name: ${values.firstName}
  - Last Name: ${values.lastName}
  - Phone Number: ${values.phone || "Not Provided"}
  - Email: ${values.email || "Not Provided"}
  - Agreed to terms: ${values.terms ? "Yes" : "No"}
`;

const usePrefill = (linkCode?: string) => {
  if (!linkCode) {
    return {
      data: { linkAppPrefill: {} } as LinkAppPrefill,
      loading: false,
      error: null,
    };
  }

  return useQuery<LinkAppPrefill, LinkAppPrefillVariables>(
    linkAppPrefillQuery,
    { variables: { linkCode } },
  );
};

const FormPage: React.FC<RouteComponentProps> = () => {
  const [errorCode, setError, setErrorCode] = useErrors();
  const [linkApp] = useMutation<LinkAppType, LinkAppVariables>(linkAppSubmissionMutation);

  const { linkCode } = useParams();
  const { data: prefillData, loading, error } = usePrefill(linkCode);

  useEffect(() => {
    if (error || (!loading && !prefillData?.linkAppPrefill)) {
      setErrorCode(error?.message as ErrorCodes);
    }
  }, [error, loading, prefillData]);

  const onSubmit: FormikConfig<Values>["onSubmit"] = async (values, formikHelpers) => {
    try {
      const { data } = await linkApp({ variables: { ...values, linkCode } });
      if (!data) {
        setError(ErrorCodes.GeneralError, formikHelpers);
        return;
      }

      navigate("/link-app/success");
    } catch (e) {
      setError((e as Error).message, formikHelpers);
    }
  };

  return (
    <>
      <div className={classNames("text-center", styles.heading)}>
        <h1 className={styles.h1}>Signup for a Haven Connect Account</h1>

        <p className={styles.largePara}>
          Creating an account lets you update your information.
        </p>

        <p className={styles.disclaimer}>
          If you provide both a mobile number and email,
          you&apos;ll be able to use either to log into your account.
        </p>
      </div>

      <div>
        {loading && <Loading />}

        {!loading && (
          <Formik
            initialValues={{
              ...initialValues,
              ...prefillData?.linkAppPrefill,
              linkCode,
            } as Values}
            validationSchema={linkAppFormSchema}
            onSubmit={onSubmit}>
            {({ values }) => (
              <>
                {errorCode === ErrorCodes.AlreadyLinked && (
                  <ErrorMessage
                    title="Already Linked"
                    emailSubject={`[Link App] ${linkCode} - Already linked`}
                  >
                    This link code has already been linked to a Haven Connect account. Please{" "}
                    <Link to="/login">Click Here</Link> to log in and update your information.
                  </ErrorMessage>
                )}

                {errorCode === ErrorCodes.UnknownCode && (
                  <ErrorMessage
                    title="Invalid Code"
                    emailSubject={`[Link App] ${linkCode} - Code not found`}
                  >
                    No records with that link code were found. Please check that your link code
                    matches exactly the code on the letter that was sent to you.
                  </ErrorMessage>
                )}

                {errorCode === ErrorCodes.DuplicateAccount && (
                  <ErrorMessage
                    title="You Already Have an Account"
                    emailSubject={`[Link App] ${linkCode} ${values.firstName} ${values.lastName} - Already have an account`}
                    emailBody={formErrorEmail("Existing account", values)}
                  >
                    Uh oh! We already have a Haven Connect account with that email address or cell phone
                    number, but it doesn&apos;t match the name you provided. Each Haven Connect account must
                    have a separate email or cell number. Please double check the spelling of your name.
                    If that doesn&apos;t work, try using a different email or cell number that belongs to you.
                  </ErrorMessage>
                )}

                {errorCode === ErrorCodes.InvalidPhoneNumber && (
                  <ErrorMessage
                    title="Unable to Create Your Account"
                    emailSubject={`[Link App] ${values.linkCode} ${values.firstName} ${values.lastName} - Invalid Data`}
                    emailBody={formErrorEmail("Invalid form data", values)}
                  >
                    There was an error with validating your information. Please review your data
                    to ensure that it is correct, and try again.
                  </ErrorMessage>
                )}

                {errorCode === ErrorCodes.GeneralError && (
                  <ErrorMessage
                    title="Error Linking your Account"
                    emailSubject={`[Link App] ${values.linkCode} ${values.firstName} ${values.lastName} - General Error`}
                    emailBody={formErrorEmail("General error", values)}
                  >
                    An unknown error has occured.
                  </ErrorMessage>
                )}

                <Form>
                  <LinkAppFields />
                </Form>
              </>
            )}
          </Formik>
        )}
      </div>
    </>
  );
};

export default FormPage;
